import { Component, ChangeDetectionStrategy, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { RouterModule } from '@angular/router';

interface UseCase {
  id: string;
  industry: string;
  title: string;
  description: string;
  icon: string;
  bgColor: string;
  bgImage: string;
  badgeColor: string;
  badgeText: string;
  metrics: { label: string; value: string }[];
  steps: string[];
  mockType: 'form' | 'queue' | 'analytics' | 'approval' | 'checklist' | 'survey';
}

@Component({
  selector: 'app-use-cases-page',
  standalone: true,
  imports: [RouterModule, NgClass],
  templateUrl: './use-cases-page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UseCasesPage {
  readonly activeIndustry = signal<string>('all');

  readonly industries = [
    { id: 'all', label: 'All Use Cases' },
    { id: 'hr', label: 'HR & People Ops' },
    { id: 'ops', label: 'Operations' },
    { id: 'it', label: 'IT & Security' },
    { id: 'sales', label: 'Sales & CRM' },
    { id: 'finance', label: 'Finance' },
    { id: 'healthcare', label: 'Healthcare' },
  ];

  readonly useCases: UseCase[] = [
    {
      id: 'employee-onboarding',
      industry: 'hr',
      title: 'Employee Onboarding',
      description:
        'Streamline new hire paperwork, collect equipment preferences, emergency contacts, and compliance acknowledgements — all in one automated flow.',
      icon: '👤',
      bgColor: 'bg-primary-50',
      bgImage: 'assets/images/landing/mountain-1.webp',
      badgeColor: 'bg-primary-100 text-primary-700',
      badgeText: 'HR & People Ops',
      metrics: [
        { label: 'Time saved per hire', value: '4.5 hrs' },
        { label: 'Completion rate', value: '98%' },
        { label: 'Forms consolidated', value: '8 → 1' },
      ],
      steps: ['Personal details', 'Emergency contacts', 'Equipment request', 'Policy sign-off'],
      mockType: 'form',
    },
    {
      id: 'it-access-request',
      industry: 'it',
      title: 'IT Access Requests',
      description:
        'Let employees request software licences, VPN access, and hardware from a single form with automatic routing to IT and manager approval.',
      icon: '🔐',
      bgColor: 'bg-secondary-50',
      bgImage: 'assets/images/landing/mountain-2.webp',
      badgeColor: 'bg-secondary-100 text-secondary-700',
      badgeText: 'IT & Security',
      metrics: [
        { label: 'Tickets reduced', value: '60%' },
        { label: 'Avg. resolution', value: '2 hrs' },
        { label: 'Auto-routed', value: '100%' },
      ],
      steps: ['Access type', 'Business justification', 'Manager approval', 'IT provisioning'],
      mockType: 'approval',
    },
    {
      id: 'vendor-onboarding',
      industry: 'finance',
      title: 'Vendor Onboarding',
      description:
        'Collect tax documents, banking details, certifications, and compliance checks from new suppliers with an automated multi-step review pipeline.',
      icon: '🏢',
      bgColor: 'bg-success-50',
      bgImage: 'assets/images/landing/forms.webp',
      badgeColor: 'bg-success-100 text-success-700',
      badgeText: 'Finance',
      metrics: [
        { label: 'Processing time', value: '-70%' },
        { label: 'Manual errors', value: '0' },
        { label: 'Compliance rate', value: '100%' },
      ],
      steps: ['Company details', 'Tax & banking', 'Certifications upload', 'Compliance review'],
      mockType: 'queue',
    },
    {
      id: 'sales-lead-intake',
      industry: 'sales',
      title: 'Sales Lead Intake',
      description:
        'Capture inbound leads from your website, events, and campaigns. Automatically score, tag, and route them to the right sales rep instantly.',
      icon: '📈',
      bgColor: 'bg-warning-50',
      bgImage: 'assets/images/landing/mountain-3.webp',
      badgeColor: 'bg-warning-100 text-warning-700',
      badgeText: 'Sales & CRM',
      metrics: [
        { label: 'Lead response time', value: '< 5 min' },
        { label: 'Conversion lift', value: '+32%' },
        { label: 'Auto-qualified', value: '85%' },
      ],
      steps: ['Contact info', 'Company & budget', 'Use case details', 'Auto CRM sync'],
      mockType: 'analytics',
    },
    {
      id: 'patient-intake',
      industry: 'healthcare',
      title: 'Patient Intake Forms',
      description:
        'Replace clipboards with digital intake: medical history, insurance info, consent signatures, and pre-visit questionnaires in one place.',
      icon: '🏥',
      bgColor: 'bg-info-50',
      bgImage: 'assets/images/landing/mountain-1.webp',
      badgeColor: 'bg-info-100 text-info-700',
      badgeText: 'Healthcare',
      metrics: [
        { label: 'Check-in time saved', value: '8 min' },
        { label: 'Data accuracy', value: '99.2%' },
        { label: 'Paper saved/yr', value: '12K sheets' },
      ],
      steps: ['Demographics', 'Medical history', 'Insurance details', 'Consent e-sign'],
      mockType: 'checklist',
    },
    {
      id: 'expense-approval',
      industry: 'finance',
      title: 'Expense Approvals',
      description:
        'Employees submit expenses with receipts. Finance and managers review, approve, and track reimbursements within policy limits automatically.',
      icon: '💳',
      bgColor: 'bg-error-50',
      bgImage: 'assets/images/landing/mountain-2.webp',
      badgeColor: 'bg-error-100 text-error-700',
      badgeText: 'Finance',
      metrics: [
        { label: 'Approval cycle', value: '1 day' },
        { label: 'Policy violations', value: '-90%' },
        { label: 'Reimbursed on time', value: '97%' },
      ],
      steps: ['Expense details', 'Receipt upload', 'Manager review', 'Finance payout'],
      mockType: 'approval',
    },
    {
      id: 'employee-survey',
      industry: 'hr',
      title: 'Employee Pulse Surveys',
      description:
        'Run recurring engagement surveys, collect anonymous feedback, and use AI to surface trends and actionable insights from responses.',
      icon: '📊',
      bgColor: 'bg-secondary-50',
      bgImage: 'assets/images/landing/forms.webp',
      badgeColor: 'bg-secondary-100 text-secondary-700',
      badgeText: 'HR & People Ops',
      metrics: [
        { label: 'Response rate', value: '78%' },
        { label: 'Insight delivery', value: 'Real-time' },
        { label: 'Action items', value: 'AI-generated' },
      ],
      steps: ['Anonymous collection', 'Likert & open text', 'AI analysis', 'Manager report'],
      mockType: 'survey',
    },
    {
      id: 'change-request',
      industry: 'it',
      title: 'Change Request Management',
      description:
        'Submit, review, approve, and track infrastructure or software changes with risk assessment, rollback plans, and full audit trails.',
      icon: '🔄',
      bgColor: 'bg-primary-50',
      bgImage: 'assets/images/landing/mountain-3.webp',
      badgeColor: 'bg-primary-100 text-primary-700',
      badgeText: 'IT & Security',
      metrics: [
        { label: 'Review time', value: '-50%' },
        { label: 'Audit compliance', value: '100%' },
        { label: 'Failed changes', value: '-40%' },
      ],
      steps: ['Change description', 'Risk assessment', 'CAB approval', 'Implementation log'],
      mockType: 'queue',
    },
    {
      id: 'facility-maintenance',
      industry: 'ops',
      title: 'Facility Maintenance Requests',
      description:
        'Staff report issues from any device. Maintenance teams receive, prioritise, assign, and close tickets with a full history log.',
      icon: '🔧',
      bgColor: 'bg-warning-50',
      bgImage: 'assets/images/landing/mountain-1.webp',
      badgeColor: 'bg-warning-100 text-warning-700',
      badgeText: 'Operations',
      metrics: [
        { label: 'Avg. resolution', value: '4 hrs' },
        { label: 'Repeat issues', value: '-55%' },
        { label: 'SLA met', value: '94%' },
      ],
      steps: ['Location & issue type', 'Photo upload', 'Priority triage', 'Technician assignment'],
      mockType: 'checklist',
    },
  ];

  getFilteredUseCases(): UseCase[] {
    const active = this.activeIndustry();
    if (active === 'all') return this.useCases;
    return this.useCases.filter((uc) => uc.industry === active);
  }

  setIndustry(id: string): void {
    this.activeIndustry.set(id);
  }
}
